import React, { useState, useEffect } from 'react';
import { HiMenu, HiX } from 'react-icons/hi';
import { Button } from '@mui/material';
import logoImage from '../../images/Logo_addox.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsOpen(false); // Close mobile menu after clicking
  };

  return (
    <nav className="fixed top-0 left-0 right-0 w-full bg-transparent z-50 transition-all duration-300">
      {/* iPad Pro at tablet: may max-width para tama ang haba ng navbar */}
      <div className="w-full max-w-none min-[768px]:max-w-4xl min-[1024px]:max-w-5xl min-[1368px]:max-w-none mx-auto px-4 sm:px-6 lg:px-16">
        {/* Rounded translucent bar - responsive height */}
        <div className={`mt-3 sm:mt-4 mb-3 sm:mb-4 rounded-xl bg-[#ffffff26] backdrop-blur-sm flex items-center px-4 sm:px-6 lg:px-8 relative transition-all duration-300 ${
          isScrolled 
            ? 'h-[70px] sm:h-[85px] lg:h-[100px]' 
            : 'h-[60px] sm:h-[65px] lg:h-[70px]'
        }`}>
            
          {/* Logo - responsive sizing */}
          <div className="flex-shrink-0 flex items-center">
            <button 
              onClick={() => scrollToSection('landing-page')}
              className="flex items-center gap-1.5 sm:gap-2 cursor-pointer"
            >
              <img 
                src={logoImage} 
                alt="The Adox Logo" 
                className={`object-contain transition-all duration-300 ${
                  isScrolled 
                    ? 'w-[50px] h-[50px] sm:w-[70px] sm:h-[70px] lg:w-[90px] lg:h-[88px]' 
                    : 'w-[45px] h-[45px] sm:w-[55px] sm:h-[55px] lg:w-[65px] lg:h-[65px]'
                }`}
              />
              {/* Text next to logo - only show when scrolled and on larger screens */}
              <div className={`flex flex-col items-start text-left transition-all duration-300 overflow-hidden ${
                isScrolled ? 'opacity-100 max-w-[200px]' : 'opacity-0 max-w-0'
              }`}>
                <div className="font-bold text-base sm:text-lg lg:text-xl tracking-tight whitespace-nowrap text-left">
                  <span className="text-[#E68A40]">The</span><span className="text-white">Adox.com</span>
                </div>
                <span className="text-white text-[10px] sm:text-xs uppercase tracking-wider whitespace-nowrap text-left">
                  VIRTUAL INTELLIGENCE
                </span>
              </div>
            </button>
          </div>

          {/* Desktop Menu - only at 1368px+ (tablet/iPad use hamburger like phone) */}
          <div className={`hidden min-[1368px]:flex absolute items-center transition-all duration-300 space-x-6 lg:space-x-10 xl:space-x-14 ${
            isScrolled 
              ? 'left-1/2 transform -translate-x-1/2' 
              : 'right-6 lg:right-12 xl:right-16'
          }`}>
            <button 
              onClick={() => scrollToSection('landing-page')}
              className="text-base lg:text-lg xl:text-xl text-white hover:text-[#E68A40] transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('what-we-offer')}
              className="text-base lg:text-lg xl:text-xl text-white hover:text-[#E68A40] transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('why-choose')}
              className="text-base lg:text-lg xl:text-xl text-white hover:text-[#E68A40] transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              About Us
            </button>
            <button 
              onClick={() => scrollToSection('footer')}
              className="text-base lg:text-lg xl:text-xl text-white hover:text-[#E68A40] transition-colors font-medium cursor-pointer whitespace-nowrap"
            >
              Contacts
            </button>
          </div>

          {/* Desktop CTA - hidden when at landing page, visible when scrolled */}
          {isScrolled && (
          <div className="hidden min-[1368px]:block ml-auto">
            <Button
                onClick={() => scrollToSection('lets-connect')}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: { xs: '0.875rem', lg: '1rem' },
                borderRadius: '12px',
                px: { xs: 2.5, lg: 3.5 },
                py: { xs: 1, lg: 1.1 },
                bgcolor: '#E68A40',
                  '&:hover': { bgcolor: '#cf7a31' },
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
              }}
            >
              INQUIRE NOW
            </Button>
          </div>
          )}

          {/* Mobile/tablet menu button - show below 1368px (phone + tablet same as phone) */}
          <div className="min-[1368px]:hidden ml-auto">
            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className="text-white hover:text-[#E68A40] transition-colors p-2"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <HiX className="h-6 w-6 sm:h-7 sm:w-7" />
              ) : (
                <HiMenu className="h-6 w-6 sm:h-7 sm:w-7" />
              )}
            </button>
          </div>

          {/* Mobile/Tablet dropdown - nakadikit sa ilalim, naka-align sa kanan (same padding as bar) */}
          <div
            className={`min-[1368px]:hidden absolute top-full right-4 sm:right-6 lg:right-8 left-auto w-44 min-w-[11rem] rounded-b-xl rounded-t-none bg-[#ffffff26] backdrop-blur-sm shadow-xl border border-t-0 border-white/20 overflow-hidden transition-all duration-200 ease-out ${
              isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
            }`}
            style={{ visibility: isOpen ? 'visible' : 'hidden' }}
          >
            <div className="px-2 py-2 space-y-0.5">
              <button 
                onClick={() => scrollToSection('landing-page')}
                className="block w-full text-right px-3 py-2 text-sm text-white hover:bg-white/10 hover:text-[#E68A40] transition-colors font-medium rounded-md"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('what-we-offer')}
                className="block w-full text-right px-3 py-2 text-sm text-white hover:bg-white/10 hover:text-[#E68A40] transition-colors font-medium rounded-md"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('why-choose')}
                className="block w-full text-right px-3 py-2 text-sm text-white hover:bg-white/10 hover:text-[#E68A40] transition-colors font-medium rounded-md"
              >
                About Us
              </button>
              <button 
                onClick={() => scrollToSection('footer')}
                className="block w-full text-right px-3 py-2 text-sm text-white hover:bg-white/10 hover:text-[#E68A40] transition-colors font-medium rounded-md"
              >
                Contacts
              </button>
              <div className="pt-1.5">
                <Button
                  onClick={() => scrollToSection('lets-connect')}
                  fullWidth
                  variant="contained"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    borderRadius: '8px',
                    py: 1,
                    bgcolor: '#E68A40',
                    '&:hover': { bgcolor: '#cf7a31' },
                    cursor: 'pointer'
                  }}
                >
                  INQUIRE NOW
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;